/**
 * Preview-mode fixtures, shaped to what the GPU pages actually read.
 *
 * Built by `buildFixtures(now)`, with every timestamp relative to `now` (the
 * moment the preview process boots), never a fixed date. Billing and status
 * logic run off timestamps: a running machine with a fixed lastChargedAt
 * would read as months unbilled (billing-on-view charges all of it wherever
 * billing is live), a `starting` machine older than 15 minutes trips the
 * stuck-start check, and every "started 2 h ago" would drift.
 *
 * The history is what production writes for a real account. Every charge is
 * computed with lib/integrations/airgpu/pricing.ts, the same functions the
 * billing routes use, and none is written as a literal. It is replayed in
 * time order the way the routes write it: hourly charges (billing-on-view), a
 * final charge on stop, `min(cost, balance)` when the wallet runs low, an
 * auto-stop when it runs out. Rows, descriptions and usage records therefore
 * have exactly the shapes production writes, and they follow any rate change
 * by themselves. The only literals are the two top-up amounts (what the
 * customer chose to pay, not a price). The external ids are mock ids so the
 * preview's AirGPU mock backs the machines; mock machines are never billed
 * and preview billing is a no-op, so a preview charges nothing new and these
 * rows are the display.
 *
 * What the pages read, so what is seeded:
 * - user + session: every signed-in page (via lib/preview/session.ts).
 * - wallets, wallet_transactions, payment_transactions: /dashboard, /wallet,
 *   /wallet/transactions (10 per page, so 11+ rows reach page 2), /api/wallet/*.
 * - billing_addresses: /wallet/topup prefill through GET /api/billing.
 * - machines: /machines, /machines/[id], /dashboard (first 4 by createdAt
 *   desc). One machine per status, plus a soft-deleted one that no page may
 *   show (every query filters deletedAt).
 * - machine_usage_records: /machines/[id] (last 10), /api/machines/[id]/usage.
 * Not seeded: products, orders and order_items (no GPU page reads them; the
 * catalogue is lib/data/gpu-products.ts), admin users, and
 * machine_state_changes (written by the API, read by nothing).
 *
 * Machines whose externalMachineId starts with `m_mock_hold_` are held: in
 * preview the AirGPU mock returns their DB state unchanged, so the
 * transitional states stay on screen for the designer. The others are
 * ordinary mock machines: Start on "Dev Box T4" animates
 * starting -> initializing -> running, Stop on "Render Box" stops it.
 *
 * The running machine's password is plaintext here and encrypted at seed time
 * (lib/preview/pglite-db.ts) with the project's own ENCRYPTION_KEY, because
 * that key is generated per project. This module is also bundled for the
 * browser (lib/auth/auth-client.ts -> session.ts), so it imports nothing
 * server-only.
 */
import type { billingAddresses, paymentTransactions, session as sessionTable, user as userTable } from "@/lib/db/schema";
import type { NewMachine, NewMachineUsageRecord } from "@/lib/db/machines-schema";
import type { wallets, walletTransactions } from "@/lib/wallet/schema";
import {
  calculateBillingMinutes,
  calculateHourlyCost,
  calculateMinimumRequiredBalance,
  calculateUsageCost,
} from "@/lib/integrations/airgpu/pricing";
import {
  AIRGPU_INSTANCE_TYPES,
  isInstanceAvailableInRegion,
  isValidSpotConfiguration,
} from "@/lib/integrations/airgpu/types";
import type {
  AirGPUInstanceType,
  AirGPUOS,
  AirGPUProvisioningType,
  AirGPURegion,
} from "@/lib/integrations/airgpu/types";

// ---------------------------------------------------------------------------
// Fixed identities the pages, probes and docs rely on
// ---------------------------------------------------------------------------

const USER_ID = "preview-user-1";
const WALLET_ID = 1;
const CURRENCY = "EUR";

/**
 * The running machine (/machines/1). The IP is in 203.0.113.0/24 (RFC 5737
 * TEST-NET-3, documentation only, never routable), so nothing a designer
 * copies out of a preview can reach a real host.
 */
export const PREVIEW_RUNNING_MACHINE = {
  id: 1,
  name: "Render Box",
  publicIp: "203.0.113.24",
  password: "Pv7kQ2mXr9Lw4Tzc",
} as const;

/** Soft-deleted fixture: must appear on no page and in no API response. */
export const PREVIEW_DELETED_MACHINE_NAME = "Old Box";

/** Top-ups the customer made (amounts they chose, not prices). */
const TOP_UPS = [
  { id: 1, daysAgo: 9, euros: 5 },
  { id: 2, daysAgo: 2, euros: 50 },
] as const;

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

// ---------------------------------------------------------------------------
// Machines
// ---------------------------------------------------------------------------

type MachineConfig = {
  instanceType: AirGPUInstanceType;
  region: AirGPURegion;
  provisioningType: AirGPUProvisioningType;
  os: AirGPUOS;
  rootVolumeSize: number;
  maxUpload: number;
};

/**
 * One running session, replayed through the billing rules. `stoppedAgo` =
 * the user pressed Stop then (final charge); `untilWalletEmpty` = it ran until
 * billing auto-stopped it; neither = still running at `now`.
 */
type SessionPlan = { startedAgo: number; stoppedAgo?: number; untilWalletEmpty?: true };

type MachinePlan = {
  id: number;
  name: string;
  status: string;
  externalMachineId: string | null;
  config: MachineConfig;
  createdAgo: number;
  /** Billed sessions, oldest first. */
  sessions?: SessionPlan[];
  /** A start that has not reached `running` (held transitional states, failures). */
  startRequestedAgo?: number;
  deletedAgo?: number;
};

// Ids are fixed so /machines/1 is the running machine. createdAt descending
// puts New Machine, Render Box, Queued Box and Training Rig on the dashboard.
// Instance types and regions are ones the catalogue sells (asserted below).
const MACHINE_PLANS: MachinePlan[] = [
  {
    id: PREVIEW_RUNNING_MACHINE.id,
    name: PREVIEW_RUNNING_MACHINE.name,
    status: "running",
    externalMachineId: "m_mock_preview_running",
    config: { instanceType: "g5.2xlarge", region: "eu-west-1", provisioningType: "on-demand", os: "windows-server-2022", rootVolumeSize: 100, maxUpload: 15 },
    createdAgo: DAY + 4 * HOUR,
    sessions: [
      { startedAgo: DAY + 3 * HOUR + 50 * MINUTE, stoppedAgo: DAY + 25 * MINUTE },
      { startedAgo: 2 * HOUR + 14 * MINUTE },
    ],
  },
  {
    id: 2,
    name: "Training Rig",
    status: "starting",
    externalMachineId: "m_mock_hold_starting",
    config: { instanceType: "g6.4xlarge", region: "eu-central-1", provisioningType: "spot", os: "windows-server-2022", rootVolumeSize: 200, maxUpload: 25 },
    createdAgo: 2 * DAY - 40 * MINUTE,
    startRequestedAgo: 40 * 1000,
  },
  {
    id: 3,
    name: "Dev Box T4",
    status: "off",
    externalMachineId: "m_mock_preview_off",
    config: { instanceType: "g4dn.2xlarge", region: "us-east-1", provisioningType: "on-demand", os: "windows-server-2022", rootVolumeSize: 100, maxUpload: 15 },
    createdAgo: 9 * DAY - 10 * MINUTE,
    // Runs on the first (small) top-up until the wallet is empty: hourly
    // charges, then a partial one and an auto-stop.
    sessions: [{ startedAgo: 9 * DAY - 15 * MINUTE, untilWalletEmpty: true }],
  },
  {
    id: 4,
    name: "Broken Box",
    status: "failed",
    externalMachineId: "m_mock_preview_failed",
    config: { instanceType: "g6.2xlarge", region: "ap-northeast-1", provisioningType: "on-demand", os: "windows-server-2022", rootVolumeSize: 100, maxUpload: 7 },
    createdAgo: 9 * DAY - 4 * MINUTE,
    startRequestedAgo: 9 * DAY - 5 * MINUTE,
  },
  {
    id: 5,
    name: "Tokyo Stream",
    status: "initializing",
    externalMachineId: "m_mock_hold_initializing",
    config: { instanceType: "g6.2xlarge", region: "ap-northeast-1", provisioningType: "on-demand", os: "windows-10-byol", rootVolumeSize: 100, maxUpload: 7 },
    createdAgo: 2 * DAY - 30 * MINUTE,
    startRequestedAgo: 3 * MINUTE,
  },
  {
    id: 6,
    name: "Batch Worker",
    status: "stopping",
    externalMachineId: "m_mock_hold_stopping",
    config: { instanceType: "g4dn.2xlarge", region: "us-west-2", provisioningType: "spot", os: "windows-server-2022", rootVolumeSize: 150, maxUpload: 15 },
    createdAgo: 2 * DAY - 10 * MINUTE,
    sessions: [{ startedAgo: 50 * MINUTE, stoppedAgo: 20 * 1000 }],
  },
  {
    id: 7,
    name: "Archive Box",
    status: "backing_up",
    externalMachineId: "m_mock_hold_backing_up",
    config: { instanceType: "g5.2xlarge", region: "eu-west-2", provisioningType: "on-demand", os: "windows-server-2022", rootVolumeSize: 300, maxUpload: 15 },
    createdAgo: 2 * DAY - 20 * MINUTE,
    sessions: [{ startedAgo: HOUR + 12 * MINUTE, stoppedAgo: 2 * MINUTE }],
  },
  {
    id: 8,
    name: "New Machine",
    status: "pending",
    externalMachineId: null, // creation call still in flight
    config: { instanceType: "g4dn.2xlarge", region: "us-east-1", provisioningType: "on-demand", os: "windows-server-2022", rootVolumeSize: 100, maxUpload: 15 },
    createdAgo: 20 * 1000,
  },
  {
    id: 9,
    name: "Queued Box",
    status: "starting_pending",
    externalMachineId: "m_mock_hold_starting_pending",
    config: { instanceType: "g6.4xlarge", region: "us-east-1", provisioningType: "on-demand", os: "windows-server-2022", rootVolumeSize: 100, maxUpload: 15 },
    createdAgo: 2 * DAY - 50 * MINUTE,
    startRequestedAgo: 5 * MINUTE,
  },
  {
    id: 10,
    name: PREVIEW_DELETED_MACHINE_NAME,
    status: "failed",
    externalMachineId: "m_mock_preview_deleted",
    config: { instanceType: "g4dn.2xlarge", region: "eu-north-1", provisioningType: "on-demand", os: "windows-server-2022", rootVolumeSize: 100, maxUpload: 7 },
    createdAgo: 9 * DAY - MINUTE,
    startRequestedAgo: 9 * DAY - 2 * MINUTE,
    deletedAgo: 8 * DAY,
  },
];

// ---------------------------------------------------------------------------
// Ledger replay
// ---------------------------------------------------------------------------

const cents = (euros: number) => Math.round(euros * 100);
const euros = (amountCents: number) => (amountCents / 100).toFixed(2);

function hourlyRate(c: MachineConfig) {
  return calculateHourlyCost(c.instanceType, c.region, c.provisioningType, c.maxUpload, c.rootVolumeSize);
}

function usageCents(c: MachineConfig, minutes: number) {
  return cents(calculateUsageCost(c.instanceType, c.region, c.provisioningType, c.maxUpload, c.rootVolumeSize, minutes));
}

function startFloorCents(c: MachineConfig) {
  return cents(calculateMinimumRequiredBalance(c.instanceType, c.region, c.provisioningType, c.maxUpload, c.rootVolumeSize));
}

// drizzle leaves identity columns out of $inferInsert; the fixtures insert
// fixed ids (pglite-db.ts relaxes the identities to BY DEFAULT for that).
type Seeded<T> = T & { id: number };
type WalletTxRow = Seeded<typeof walletTransactions.$inferInsert>;
type PaymentRow = Seeded<typeof paymentTransactions.$inferInsert>;

/** Live state of one billed session while the replay runs. */
type SessionState = {
  plan: SessionPlan;
  machine: MachinePlan;
  startedAt: Date;
  stopAt: Date | null;
  chargedFrom: Date;
  charged: boolean;
  done: boolean;
  endedAt: Date | null;
};

export function buildFixtures(now: Date) {
  const ago = (ms: number) => new Date(now.getTime() - ms);
  const later = (ms: number) => new Date(now.getTime() + ms);

  let balanceCents = 0;
  const walletTxs: WalletTxRow[] = [];
  const usage: Seeded<NewMachineUsageRecord>[] = [];
  const payments: PaymentRow[] = [];
  const problems: string[] = [];

  const requireFloor = (m: MachinePlan, what: string, at: Date) => {
    const floor = startFloorCents(m.config);
    if (balanceCents < floor) {
      problems.push(`${m.name}: ${what} at ${at.toISOString()} needs ${euros(floor)} in the wallet, has ${euros(balanceCents)}`);
    }
  };

  /** One charge exactly as the routes write it. Returns false when the wallet could not cover it. */
  const charge = (s: SessionState, periodEnd: Date, kind: "hourly" | "final"): boolean => {
    const m = s.machine;
    const billingMinutes = calculateBillingMinutes(s.chargedFrom, periodEnd);
    const costCents = usageCents(m.config, billingMinutes);
    if (costCents <= 0) {
      // The routes skip a zero charge; flag it rather than replay a free machine.
      problems.push(`${m.name}: ${billingMinutes} min priced at ${euros(costCents)}`);
      s.chargedFrom = periodEnd;
      return true;
    }

    const chargeCents = Math.min(costCents, balanceCents);
    const insufficient = balanceCents < costCents;
    if (chargeCents > 0) {
      balanceCents -= chargeCents;
      const walletTransactionId = walletTxs.length + 1;
      const label = kind === "hourly" ? `${billingMinutes} min` : `final charge - ${billingMinutes} min`;
      const suffix = !insufficient ? "" : kind === "hourly" ? " - PARTIAL, machine auto-stopped" : " - PARTIAL";
      walletTxs.push({
        id: walletTransactionId,
        walletId: WALLET_ID,
        userId: USER_ID,
        paymentTransactionId: null,
        amount: euros(-chargeCents),
        currency: CURRENCY,
        type: "debit",
        description: `GPU usage: ${m.name} (${label})${suffix}`,
        exchangeRate: null,
        createdAt: periodEnd,
      });
      usage.push({
        id: usage.length + 1,
        machineId: m.id,
        userId: USER_ID,
        periodStart: s.chargedFrom,
        periodEnd,
        billingMinutes,
        costPerHour: hourlyRate(m.config).toFixed(4),
        totalCost: euros(chargeCents),
        walletId: WALLET_ID,
        walletTransactionId,
        chargedAt: periodEnd,
        status: insufficient ? "partial" : "charged",
        instanceType: m.config.instanceType,
        region: m.config.region,
        createdAt: periodEnd,
      });
    }
    s.chargedFrom = periodEnd;
    s.charged = true;
    return !insufficient;
  };

  // Every dated thing that touches the wallet or depends on it, replayed in
  // time order so a partial charge sees the balance it would have seen.
  type Event =
    | { at: Date; kind: "topup"; topUp: (typeof TOP_UPS)[number] }
    | { at: Date; kind: "check"; machine: MachinePlan; what: string }
    | { at: Date; kind: "session"; plan: SessionPlan; machine: MachinePlan };
  const events: Event[] = [];
  for (const t of TOP_UPS) events.push({ at: ago(t.daysAgo * DAY), kind: "topup", topUp: t });
  for (const m of MACHINE_PLANS) {
    events.push({ at: ago(m.createdAgo), kind: "check", machine: m, what: "create" });
    if (m.startRequestedAgo !== undefined) {
      events.push({ at: ago(m.startRequestedAgo), kind: "check", machine: m, what: "start" });
    }
    for (const plan of m.sessions ?? []) {
      events.push({ at: ago(plan.startedAgo), kind: "session", plan, machine: m });
    }
  }
  events.sort((a, b) => a.at.getTime() - b.at.getTime());

  const sessions: SessionState[] = [];

  // Next billing step of an open session: an hourly charge once an hour has
  // passed since the last one, else the final charge at its stop.
  const nextStep = (s: SessionState): { at: Date; kind: "hourly" | "final" } | null => {
    if (s.done) return null;
    const tick = new Date(s.chargedFrom.getTime() + HOUR);
    const horizon = s.stopAt ?? now;
    if (s.stopAt ? tick < horizon : tick <= horizon) return { at: tick, kind: "hourly" };
    if (s.stopAt) return { at: s.stopAt, kind: "final" };
    return null;
  };

  const runSessionsUntil = (until: Date) => {
    for (;;) {
      let pick: { s: SessionState; step: { at: Date; kind: "hourly" | "final" } } | null = null;
      for (const s of sessions) {
        const step = nextStep(s);
        if (step && step.at <= until && (!pick || step.at < pick.step.at)) pick = { s, step };
      }
      if (!pick) return;
      const { s, step } = pick;
      const covered = charge(s, step.at, step.kind);
      if (step.kind === "final" || !covered) {
        // A stop, or the billing-on-view auto-stop when the wallet ran out.
        s.done = true;
        s.endedAt = step.at;
      }
    }
  };

  for (const e of events) {
    runSessionsUntil(e.at);
    if (e.kind === "topup") {
      const amountCents = cents(e.topUp.euros);
      balanceCents += amountCents;
      payments.push({
        id: e.topUp.id,
        userId: USER_ID,
        gatewayTransactionId: `preview_pt_${String(e.topUp.id).padStart(3, "0")}`,
        gatewaySignature: null,
        amount: euros(amountCents),
        currency: CURRENCY,
        status: "paid",
        checkoutUrl: null,
        webhookPayload: null,
        initiatedAt: new Date(e.at.getTime() - 4 * MINUTE),
        completedAt: e.at,
        orderId: null,
        source: "wallet-topup",
      });
      walletTxs.push({
        id: walletTxs.length + 1,
        walletId: WALLET_ID,
        userId: USER_ID,
        paymentTransactionId: e.topUp.id,
        amount: euros(amountCents),
        currency: CURRENCY,
        type: "credit",
        description: "Wallet top-up via payment gateway",
        exchangeRate: null,
        createdAt: e.at,
      });
    } else if (e.kind === "check") {
      requireFloor(e.machine, e.what, e.at);
    } else {
      requireFloor(e.machine, "start", e.at);
      const stopAt = e.plan.stoppedAgo === undefined ? null : ago(e.plan.stoppedAgo);
      sessions.push({
        plan: e.plan,
        machine: e.machine,
        startedAt: e.at,
        stopAt,
        chargedFrom: e.at,
        charged: false,
        done: false,
        endedAt: null,
      });
    }
  }
  runSessionsUntil(now);

  // ---- machines rows, from the plans plus what the replay did -------------

  const machines: Seeded<NewMachine>[] = MACHINE_PLANS.map((m) => {
    const mine = sessions.filter((s) => s.machine.id === m.id);
    const ended = mine.filter((s) => s.endedAt);
    const open = mine.find((s) => !s.endedAt);
    const last = mine[mine.length - 1];
    const startRequestedAt = m.startRequestedAgo !== undefined ? ago(m.startRequestedAgo) : null;
    const lastStopped = ended.length ? ended[ended.length - 1].endedAt : null;
    const running = m.status === "running";
    const spec = AIRGPU_INSTANCE_TYPES[m.config.instanceType];

    if (running !== Boolean(open)) {
      problems.push(`${m.name}: status ${m.status} but ${open ? "an open" : "no open"} session after the replay`);
    }
    for (const s of mine) {
      if (s.plan.untilWalletEmpty && !s.endedAt) {
        problems.push(`${m.name}: the wallet never ran out, so the planned auto-stop did not happen`);
      }
    }

    // The start route resets lastChargedAt; billing then sets it on every
    // charge, stop and auto-stop.
    const lastChargedAt = open ? (open.charged ? open.chargedFrom : null) : lastStopped;
    const updatedAt = [ago(m.createdAgo), startRequestedAt, last?.chargedFrom ?? null, lastStopped]
      .filter((d): d is Date => d !== null)
      .reduce((a, b) => (a > b ? a : b));

    return {
      id: m.id,
      userId: USER_ID,
      externalMachineId: m.externalMachineId,
      name: m.name,
      status: m.status,
      region: m.config.region,
      instanceType: m.config.instanceType,
      provisioningType: m.config.provisioningType,
      os: m.config.os,
      vcpus: spec.vcpus,
      ram: spec.ram,
      rootVolumeSize: m.config.rootVolumeSize,
      maxUpload: m.config.maxUpload,
      // Connection details exist only while running; stop clears them.
      publicIp: running ? PREVIEW_RUNNING_MACHINE.publicIp : null,
      password: null, // the running machine's is encrypted at seed time
      lastStartedAt: startRequestedAt ?? last?.startedAt ?? null,
      lastStoppedAt: lastStopped,
      lastChargedAt,
      totalRunningMinutes: ended.reduce(
        (sum, s) => sum + calculateBillingMinutes(s.startedAt, s.endedAt as Date),
        0,
      ),
      metadata: null,
      deletedAt: m.deletedAgo !== undefined ? ago(m.deletedAgo) : null,
      createdAt: ago(m.createdAgo),
      updatedAt,
    };
  });

  // ---- invariants ----------------------------------------------------------

  for (const m of MACHINE_PLANS) {
    const c = m.config;
    if (!isInstanceAvailableInRegion(c.instanceType, c.region)) {
      problems.push(`${m.name}: ${c.instanceType} is not sold in ${c.region}`);
    }
    if (!isValidSpotConfiguration(c.provisioningType, c.os)) {
      problems.push(`${m.name}: spot is not available with ${c.os}`);
    }
  }
  const ledgerCents = walletTxs.reduce((sum, t) => sum + cents(Number(t.amount)), 0);
  if (ledgerCents !== balanceCents || balanceCents < 0) {
    problems.push(`wallet balance ${euros(balanceCents)} != sum of transactions ${euros(ledgerCents)}`);
  }
  // Start and Create must work from the seeded wallet (3-hour floor of every
  // fixture config), and /machines must not open on the low-balance banner (< 5).
  for (const m of MACHINE_PLANS) requireFloor(m, "start from the seeded wallet", now);
  if (balanceCents < cents(5)) problems.push(`wallet balance ${euros(balanceCents)} triggers the low-balance banner`);
  if (walletTxs.length <= 10) problems.push(`${walletTxs.length} wallet transactions do not reach /wallet/transactions page 2`);

  // ---- the rest of the account ---------------------------------------------

  const userCreatedAt = ago(10 * DAY);

  const user = {
    id: USER_ID,
    name: "Preview User",
    email: "preview@example.com",
    emailVerified: true,
    image: null,
    createdAt: userCreatedAt,
    updatedAt: userCreatedAt,
    velvexpayClientId: null,
  } satisfies typeof userTable.$inferInsert;

  const session = {
    id: "preview-session-1",
    expiresAt: later(365 * DAY),
    token: "preview-session-token-abc123xyz",
    createdAt: ago(DAY),
    updatedAt: ago(HOUR),
    ipAddress: null,
    userAgent: null,
    userId: USER_ID,
  } satisfies typeof sessionTable.$inferInsert;

  const wallet = {
    id: WALLET_ID,
    userId: USER_ID,
    balance: euros(balanceCents),
    currency: CURRENCY,
    status: "active",
    createdAt: userCreatedAt,
    updatedAt: walletTxs.reduce((a, t) => (t.createdAt && t.createdAt > a ? t.createdAt : a), userCreatedAt),
  } satisfies Seeded<typeof wallets.$inferInsert>;

  const billingAddress = {
    id: 1,
    userId: USER_ID,
    firstName: "Preview",
    lastName: "User",
    email: "preview@example.com",
    phone: "+31 20 123 4567",
    streetAddress: "1 Example Street",
    city: "Amsterdam",
    country: "NL",
    zipCode: "1011 AB",
    state: null,
    isDefault: true,
    createdAt: ago(9 * DAY + 5 * MINUTE),
    updatedAt: ago(9 * DAY + 5 * MINUTE),
  } satisfies Seeded<typeof billingAddresses.$inferInsert>;

  return {
    user,
    session,
    wallet,
    paymentTransactions: payments,
    walletTransactions: walletTxs,
    billingAddresses: [billingAddress],
    machines,
    machineUsageRecords: usage,
    /** Empty when the fixtures are consistent; pglite-db.ts refuses to seed otherwise. */
    problems,
  };
}

export type PreviewFixtures = ReturnType<typeof buildFixtures>;

/** Built once per process, at boot. session.ts and pglite-db.ts share this instance. */
export const fixtures: PreviewFixtures = buildFixtures(new Date());
